import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { api, API_URL } from '../../api/client';

const TYPE_ORDER = ['table', 'view', 'visualization', 'dashboard'];
const COL_X = [60, 300, 540, 780];
const NODE_W = 200;
const NODE_H = 52;
const ROW_GAP = 72;
const PAD_Y = 48;

const TYPE_COLORS = {
  table: '#3b82f6',
  view: '#6366f1',
  visualization: '#8b5cf6',
  dashboard: '#ec4899',
};

const STATUS_COLORS = {
  stale: '#f59e0b',
  failed: '#ef4444',
};

// Parse a qualified table key like "sf_00D5e000002GFK2EAO__Account"
// into { source: "Salesforce", object: "Account" }.
// For bare names with no __ separator, returns { source: null, object: key }.
function parseTableKey(key) {
  if (!key) return { source: null, object: key || '' };
  const idx = key.indexOf('__');
  if (idx === -1) return { source: null, object: key };
  const prefix = key.slice(0, idx);
  const object = key.slice(idx + 2);
  const source = prefix.startsWith('sf_') ? 'Salesforce'
    : prefix.startsWith('hs_') ? 'HubSpot'
    : prefix;
  return { source, object };
}

function truncate(s, max = 22) {
  return s && s.length > max ? s.slice(0, max - 1) + '\u2026' : s;
}

export default function DependencyGraphTab() {
  const { activeTenant } = useSelector((state) => state.auth);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sseConnected, setSSEConnected] = useState(false);
  const [hiddenKeys, setHiddenKeys] = useState(new Set());
  const [positions, setPositions] = useState({});   // nodeKey → {x, y} drag overrides
  const [dragging, setDragging] = useState(null);   // {key, offsetX, offsetY}
  const svgRef = useRef(null);

  const tenantId = activeTenant?.tenant_id;

  const fetchDAG = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`${API_URL}/api/v1/analytics/dependencies/dag`, { tenantId });
      setEdges(Array.isArray(data?.edges) ? data.edges : []);
    } catch (err) {
      setError(err.message || 'Failed to load dependency graph');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { fetchDAG(); }, [fetchDAG]);

  useEffect(() => {
    if (!tenantId) return;
    let reconnectTimer;
    let es;
    const connect = () => {
      es = new EventSource(`${API_URL}/api/v1/analytics/events`, { withCredentials: true });
      es.onopen = () => setSSEConnected(true);
      es.addEventListener('dag_changed', () => fetchDAG());
      es.onerror = () => {
        setSSEConnected(false);
        es.close();
        reconnectTimer = setTimeout(connect, 60000);
      };
    };
    connect();
    return () => { if (es) es.close(); clearTimeout(reconnectTimer); };
  }, [tenantId, fetchDAG]);

  // All nodes derived from edges
  const allNodes = useMemo(() => {
    const seen = new Map();
    for (const e of edges) {
      const fk = `${e.from_type}:${e.from_id || e.from_name}`;
      const tk = `${e.to_type}:${e.to_id || e.to_name}`;
      if (!seen.has(fk)) seen.set(fk, { key: fk, type: e.from_type, name: e.from_name, status: e.from_status, staleReason: e.from_stale_reason });
      if (!seen.has(tk)) seen.set(tk, { key: tk, type: e.to_type, name: e.to_name, status: e.to_status, staleReason: e.to_stale_reason });
    }
    return [...seen.values()];
  }, [edges]);

  // Nodes grouped by type for the filter panel
  const nodesByType = useMemo(() => {
    const grouped = {};
    for (const type of TYPE_ORDER) grouped[type] = [];
    for (const n of allNodes) (grouped[n.type] || grouped.table).push(n);
    return grouped;
  }, [allNodes]);

  // Visible nodes and edges after applying filter
  const { nodes, nodeMap, svgH, svgW, visibleEdges } = useMemo(() => {
    const visible = allNodes.filter(n => !hiddenKeys.has(n.key));
    const visibleSet = new Set(visible.map(n => n.key));

    const cols = { table: [], view: [], visualization: [], dashboard: [] };
    for (const n of visible) (cols[n.type] || cols.table).push(n);

    const maxPerCol = Math.max(1, ...Object.values(cols).map(c => c.length));
    const totalH = maxPerCol * ROW_GAP + PAD_Y * 2;
    const totalW = COL_X[COL_X.length - 1] + NODE_W + 60;

    const map = new Map();
    for (const type of TYPE_ORDER) {
      const col = cols[type];
      const colIdx = TYPE_ORDER.indexOf(type);
      const startY = (totalH - col.length * ROW_GAP) / 2 + ROW_GAP / 2;
      col.forEach((n, i) => {
        n.x = COL_X[colIdx];
        n.y = startY + i * ROW_GAP;
        map.set(n.key, n);
      });
    }

    const ve = edges.filter(e => {
      const fk = `${e.from_type}:${e.from_id || e.from_name}`;
      const tk = `${e.to_type}:${e.to_id || e.to_name}`;
      return visibleSet.has(fk) && visibleSet.has(tk);
    });

    return { nodes: visible, nodeMap: map, svgH: totalH, svgW: totalW, visibleEdges: ve };
  }, [allNodes, hiddenKeys, edges]);

  const toggleNode = (key) => setHiddenKeys(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const toggleGroup = (type) => {
    const groupKeys = nodesByType[type]?.map(n => n.key) ?? [];
    const allHidden = groupKeys.every(k => hiddenKeys.has(k));
    setHiddenKeys(prev => {
      const next = new Set(prev);
      allHidden ? groupKeys.forEach(k => next.delete(k)) : groupKeys.forEach(k => next.add(k));
      return next;
    });
  };

  // Reset drag positions when visible node set changes (filter toggled)
  const prevNodeKeysRef = useRef('');
  const nodeKeysStr = nodes.map(n => n.key).sort().join(',');
  if (nodeKeysStr !== prevNodeKeysRef.current) {
    prevNodeKeysRef.current = nodeKeysStr;
    if (Object.keys(positions).length > 0) setPositions({});
  }

  // Get effective position: drag override or layout position
  const getPos = (n) => positions[n.key] || { x: n.x, y: n.y };

  const getSVGPoint = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  const handleNodeMouseDown = (e, n) => {
    e.preventDefault();
    e.stopPropagation();
    const svgP = getSVGPoint(e);
    if (!svgP) return;
    const pos = getPos(n);
    setDragging({ key: n.key, offsetX: svgP.x - pos.x, offsetY: svgP.y - pos.y });
  };

  const handleSVGMouseMove = (e) => {
    if (!dragging) return;
    const svgP = getSVGPoint(e);
    if (!svgP) return;
    setPositions(prev => ({
      ...prev,
      [dragging.key]: { x: svgP.x - dragging.offsetX, y: svgP.y - dragging.offsetY },
    }));
  };

  const handleSVGMouseUp = () => setDragging(null);

  // Display label for a node: tables get "Source · Object", others get their name
  const nodeLabel = (n) => {
    if (n.type === 'table') {
      const { source, object } = parseTableKey(n.name);
      return source ? { top: source, bottom: object } : { top: null, bottom: n.name };
    }
    return { top: null, bottom: n.name };
  };

  if (loading && edges.length === 0) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500 text-sm">Loading dependency graph...</div>;
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center text-red-600 text-sm mb-3">{error}</div>
        <div className="text-center"><button onClick={fetchDAG} className="text-sm text-primary-600 hover:text-primary-700 font-medium">Retry</button></div>
      </div>
    );
  }

  if (allNodes.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-700 font-medium mb-1">No dependencies yet</p>
        <p className="text-gray-500 text-sm">Create views that query synced tables to see the dependency graph.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="font-bold text-gray-900">Dependency Graph</h2>
          <p className="text-sm text-gray-500 mt-0.5">Lineage from synced tables through views, visualizations, and dashboards</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1.5 text-xs ${sseConnected ? 'text-green-600' : 'text-gray-400'}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${sseConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
            {sseConnected ? 'Live' : 'Disconnected'}
          </span>
          <button onClick={fetchDAG} disabled={loading} className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Filter panel */}
        <div className="w-52 shrink-0 border-r border-gray-100 p-3 overflow-y-auto" style={{ maxHeight: 600 }}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Filter</p>
          {TYPE_ORDER.map(type => {
            const group = nodesByType[type];
            if (!group?.length) return null;
            const allHidden = group.every(n => hiddenKeys.has(n.key));
            const someHidden = group.some(n => hiddenKeys.has(n.key));
            return (
              <div key={type} className="mb-3">
                <button
                  onClick={() => toggleGroup(type)}
                  className="flex items-center gap-1.5 w-full text-left mb-1 group"
                >
                  <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: TYPE_COLORS[type] }} />
                  <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900">
                    {type.charAt(0).toUpperCase() + type.slice(1)}s
                  </span>
                  <span className="ml-auto text-xs text-gray-400">
                    {allHidden ? 'show' : someHidden ? 'mix' : 'hide'}
                  </span>
                </button>
                <div className="pl-4 space-y-0.5">
                  {group.map(n => {
                    const { top, bottom } = nodeLabel(n);
                    const label = top ? `${top} · ${bottom}` : bottom;
                    const hidden = hiddenKeys.has(n.key);
                    return (
                      <label key={n.key} className="flex items-center gap-1.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={!hidden}
                          onChange={() => toggleNode(n.key)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-3 h-3"
                        />
                        <span className={`text-xs truncate ${hidden ? 'text-gray-300' : 'text-gray-600'}`} title={label}>
                          {truncate(label, 20)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Graph */}
        <div className="flex-1 overflow-auto p-4">
          {nodes.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">All nodes hidden — use the filter to show some.</div>
          ) : (
            <svg
              ref={svgRef}
              viewBox={`0 0 ${svgW} ${svgH}`}
              width={svgW}
              height={svgH}
              className="block"
              style={{ minWidth: svgW, userSelect: 'none', cursor: dragging ? 'grabbing' : 'default' }}
              onMouseMove={handleSVGMouseMove}
              onMouseUp={handleSVGMouseUp}
              onMouseLeave={handleSVGMouseUp}
            >
              <defs>
                <marker id="arrow" viewBox="0 0 10 7" refX="10" refY="3.5" markerWidth="8" markerHeight="6" orient="auto-start-reverse">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#9ca3af" />
                </marker>
              </defs>

              {/* Edges */}
              {visibleEdges.map((e, i) => {
                const fk = `${e.from_type}:${e.from_id || e.from_name}`;
                const tk = `${e.to_type}:${e.to_id || e.to_name}`;
                const src = nodeMap.get(fk);
                const tgt = nodeMap.get(tk);
                if (!src || !tgt) return null;
                const sp = getPos(src);
                const tp = getPos(tgt);
                const x1 = sp.x + NODE_W;
                const y1 = sp.y;
                const x2 = tp.x;
                const y2 = tp.y;
                const dx = (x2 - x1) * 0.4;
                return (
                  <path key={i} d={`M${x1},${y1} C${x1+dx},${y1} ${x2-dx},${y2} ${x2},${y2}`}
                    stroke="#9ca3af" strokeWidth={1.5} fill="none" markerEnd="url(#arrow)" />
                );
              })}

              {/* Nodes */}
              {nodes.map(n => {
                const { top, bottom } = nodeLabel(n);
                const fill = STATUS_COLORS[n.status] || TYPE_COLORS[n.type] || '#6b7280';
                const { x: nx, y: cy } = getPos(n);
                const isDraggingThis = dragging?.key === n.key;
                return (
                  <g key={n.key}
                    style={{ cursor: isDraggingThis ? 'grabbing' : 'grab' }}
                    onMouseDown={(e) => handleNodeMouseDown(e, n)}
                  >
                    <title>{top ? `${top} · ${bottom}` : bottom}{n.status ? ` (${n.status})` : ''}{n.staleReason ? `: ${n.staleReason}` : ''}</title>
                    <rect x={nx} y={cy - NODE_H / 2} width={NODE_W} height={NODE_H} rx={6} fill={fill}
                      opacity={isDraggingThis ? 0.85 : 1}
                      filter={isDraggingThis ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : undefined}
                    />
                    {top ? (
                      <>
                        <text x={nx + NODE_W / 2} y={cy - 7} textAnchor="middle" fontSize={10}
                          fill="rgba(255,255,255,0.75)" fontFamily="system-ui, sans-serif" style={{ pointerEvents: 'none' }}>
                          {truncate(top, 24)}
                        </text>
                        <text x={nx + NODE_W / 2} y={cy + 10} textAnchor="middle" fontSize={13}
                          fontWeight="600" fill="#ffffff" fontFamily="system-ui, sans-serif" style={{ pointerEvents: 'none' }}>
                          {truncate(bottom, 22)}
                        </text>
                      </>
                    ) : (
                      <text x={nx + NODE_W / 2} y={cy + 5} textAnchor="middle" fontSize={13}
                        fill="#ffffff" fontFamily="system-ui, sans-serif" style={{ pointerEvents: 'none' }}>
                        {truncate(bottom, 22)}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}
